import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActiveToast } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { Logger } from 'app/core/logger/services/logger.service';
import { LocalizedToasterService } from 'app/core/toaster/localized-toaster.service';
import { DataTrackLabelEnum } from 'app/shared/enums/data-track-label.enum';
import { KhAnalyticsService } from 'app/shared/services/kh-analytics.service';

import { environment } from 'env/environment';

@Injectable()
export class ClientVersionInterceptor implements HttpInterceptor {
  private readonly localStorageKey = 'KnowledgehookPlatformVersionMismatchToastTime';
  private readonly analyticsTrackIntervalHours = 12; // in hours
  private readonly logger = inject(Logger);
  private readonly toasterService = inject(LocalizedToasterService);
  private readonly analyticsService = inject(KhAnalyticsService);
  private readonly translateService = inject(TranslateService);

  private _activeToast: ActiveToast<unknown> | null = null;

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      tap((event: HttpEvent<unknown>) => {
        if (environment.clientVersion !== null && event instanceof HttpResponse && event.headers.has('X-KH-Client-Version')) {
          const serverVersion = event.headers.get('X-KH-Client-Version');

          if (serverVersion !== null && this.checkShouldToast(serverVersion)) {
            this.doToast(serverVersion, environment.clientVersion);
          }
        }
      })
    );
  }

  private checkShouldToast(serverVersion: string): boolean {
    // If ClientVersion is not set don't toast or environment is local
    if (environment.local || environment.clientVersion === null) {
      return false;
    }

    return serverVersion !== environment.clientVersion;
  }

  private doToast(serverVersion: string, clientVersion: string): void {
    if (!this._activeToast) {
      this.doToastAnalytics(serverVersion, clientVersion);
      this._activeToast = this.toasterService.warn(
        this.translateService.instant('CLIENT_VERSION.KNOWLEDGEHOOK_HAS_RECENTLY_UPDATED') as string,
        this.translateService.instant('CLIENT_VERSION.CLICK_HERE_TO_REFRESH_THE') as string,
        { disableTimeOut: true });

      this._activeToast.onHidden.subscribe(() => {
        this._activeToast = null;
        this.analyticsService.trackEventSimple(DataTrackLabelEnum.PlatformVersionMismatchReload);
      });

      this._activeToast.onTap.subscribe(() => {
        this.hardReload();
      });
    }
  }

  private hardReload() {
    const urlObj = new URL(window.location.href);

    // set a query param to the current time to force a cache bust reload
    urlObj.searchParams.set('t', new Date().getTime().toString());
    window.location.href = urlObj.href;
  }

  /*
   *  We only want to track this analytic at the time interval set in analyticsTrackIntervalHours
   */
  private doToastAnalytics(serverVersion: string, clientVersion: string): void {
    let shouldFireAnalytics = false;

    const localStorageDateString = localStorage.getItem(this.localStorageKey);

    if (localStorageDateString) {
      const lastToastTime = new Date(localStorageDateString);
      const now = new Date();
      const diff = now.getTime() - lastToastTime.getTime();
      const hours = diff / 3600000; // convert to hours
      shouldFireAnalytics = hours >= this.analyticsTrackIntervalHours;
    } else {
      shouldFireAnalytics = true;
      localStorage.setItem(this.localStorageKey, new Date().toISOString());
    }

    this.logger.warn(`Client version mismatch. Server version: ${serverVersion}, Client version: ${clientVersion}`);

    if (shouldFireAnalytics) {
      this.analyticsService.trackEventSimple(DataTrackLabelEnum.PlatformVersionMismatchToast);
    }
  }
}
